
import { PropType, defineComponent } from 'vue';

import { GOOGLE_MAPS_SETTINGS } from '@/shared/constants';
import googleMapsApi from '@/shared/modules/googleMapsApi';

import CenterMapControl from './CenterMapControl.vue';
import DownloadFileMapControl from './DownloadFileMapControl.vue';
import InfoModal from './InfoModal.vue';
import LocationMarker from './LocationMarker';
import RiskPill from './RiskPill.vue';

export interface GeolocationPositionError {
  code: number;
  message: string;
}

const initialPosition = {
  lat: 47.561838,
  lng: 14.633302,
};

export default defineComponent({
  name: 'GeoJsonMap',
  components: { DownloadFileMapControl, CenterMapControl, RiskPill, InfoModal },
  props: {
    controls: {
      type: Array as PropType<string[]>,
      default: () => ['searchField', 'mapType', 'zoom'],
    },
    data: {
      type: Object,
      default: () => {},
    },
  },

  inject: {
    googleMapsSettings: {
      default: () => GOOGLE_MAPS_SETTINGS,
    },
  },

  data() {
    return {
      map: null as google.maps.Map | null,
      google: null as any,
      currentLocation: null as { lat: any; lng: any } | null,
      mapDataBounds: null,
      centerControlActionType: 'currentLocation',
      riskCategory: null as string | null,
      riskInfoText: null as string | null,
      occurrencesPerHa: null as number | null,
      modalVisible: false as boolean,
    };
  },

  async mounted() {
    await this.mapInit();
    await this.loadGeojson(this.data);
  },
  methods: {
    async mapInit() {
      this.google = await googleMapsApi();
      const options = {
        center: initialPosition,
        zoom: 16,
        tilt: 0,
        mapTypeId: this.google.maps.MapTypeId.HYBRID,
        disableDefaultUI: true,
        mapTypeControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: this.google.maps.ControlPosition.RIGHT_BOTTOM,
        },
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
      };
      this.map = new this.google.maps.Map(this.$refs['map-container'], options);

      this.displayCurrentLocation();
      this.map?.addListener('center_changed', () => {
        this.centerControlActionType = 'currentLocation';
      });
    },

    async loadGeojson(data: any) {
      if (!data) return;

      const props = data.properties;

      if (props) {
        if ('risk-category' in props) {
          this.riskCategory = props['risk-category'];
        }

        if ('display-risk-info' in props) {
          this.riskInfoText = props['display-risk-info'];
        }

        if ('occurrences-per-ha' in props) {
          this.occurrencesPerHa = props['occurrences-per-ha'];
        }
      }

      await this.map?.data.addGeoJson(data);
      this.map?.data.setStyle((feature) => {
        const fillColor = feature.getProperty('fill');
        const fillOpacity = feature.getProperty('fill-opacity');
        const strokeColor = feature.getProperty('stroke');
        const strokeWeight = feature.getProperty('stroke-width');
        const strokeOpacity = feature.getProperty('stroke-opacity');

        return {
          fillColor: typeof fillColor === 'string' ? fillColor : undefined,
          fillOpacity: typeof fillOpacity === 'number' ? fillOpacity : undefined,
          strokeColor: typeof strokeColor === 'string' ? strokeColor : undefined,
          strokeWeight: typeof strokeWeight === 'number' ? strokeWeight : undefined,
          strokeOpacity: typeof strokeOpacity === 'number' ? strokeOpacity : undefined,
        };
      });

      this.computeDataMapBounds();
      this.mapFitBoundsToData();
      await this.$nextTick();
    },

    mapFitBoundsToData() {
      if (this.mapDataBounds) {
        this.map?.fitBounds(this.mapDataBounds);
      }
    },

    computeDataMapBounds() {
      const bounds = new this.google.maps.LatLngBounds();
      this.map?.data.forEach((feature) => {
        feature.getGeometry()?.forEachLatLng((latLng) => {
          bounds.extend(latLng);
        });
      });
      this.mapDataBounds = bounds;
    },

    async displayCurrentLocation() {
      try {
        if (!navigator.geolocation) {
          throw new Error('Geolocation not available');
        }

        const marker = LocationMarker({ google: this.google, map: this.map });

        await this.watchCurrentLocation({
          onSuccess: ({ coords: { latitude: lat, longitude: lng, accuracy } }) => {
            marker.innerCircle.setPosition({ lat, lng });
            marker.outerCircle.setRadius(accuracy);
            this.currentLocation = { lat, lng };
          },
          onError: (err: any) => {
            throw new Error(`Error: ${err.code}: ${this.getPositionErrorMessage(err.code)}`);
          },
        });
      } catch (error: any) {
        console.error(`Could not retrieve current location: ${error.message}`);
      }
    },

    async watchCurrentLocation({
      onSuccess,
      onError,
    }: {
      onSuccess: (position: any) => void;
      onError: (error: GeolocationPositionError) => void;
    }) {
      await navigator.geolocation.watchPosition(onSuccess, onError, {
        enableHighAccuracy: true,
      });
    },

    async focusCurrentLocation() {
      try {
        if (!navigator.geolocation) {
          throw new Error('Geolocation not available');
        }

        if (!this.currentLocation) {
          throw new Error('Could not retrieve current location.');
        }

        this.map?.panTo(this.currentLocation);
      } catch (error: any) {
        console.error(`Could not focus current location: ${error.message}`);
      }
    },

    getPositionErrorMessage(code: number) {
      switch (code) {
        case 1:
          return 'Permission denied.';
        case 2:
          return 'Position unavailable.';
        case 3:
          return 'Timeout reached.';
        default:
          return 'unknown error';
      }
    },

    centerMap() {
      switch (this.centerControlActionType) {
        case 'mapData':
          this.mapFitBoundsToData();
          this.centerControlActionType = 'currentLocation';
          return;
        case 'currentLocation':
          this.focusCurrentLocation();
          this.centerControlActionType = 'mapData';
          return;
        default:
          this.mapFitBoundsToData();
          this.centerControlActionType = 'currentLocation';
      }
    },
  },
  watch: {
    riskCategory(newValue) {
      if (newValue === 'high') {
        this.modalVisible = true;
      }
    },
  },
});
